// src/pages/tournament/useRanking.js
// The standings of a tournament (best first, with how far each player moved since the previous round).
// Refetches when `version` changes (something the ranking depends on was changed) and when the test panel
// changes data. Set `enabled` to false to not fetch while nobody is looking at it.
import { useCallback, useEffect, useState } from 'react';
import { apiRequest } from '../../api';
import { useDataChanged } from '../../testMode/testMode';

export function useRanking(tournamentId, user, { version = 0, enabled = true, byTeam = false } = {}) {
    const userId = user?.uid;
    const [standings, setStandings] = useState([]);
    const [tournament, setTournament] = useState(null);
    const [ranking, setRanking] = useState(null); // what the score is (method, unit, decimals)
    const [loading, setLoading] = useState(true);
    const [failed, setFailed] = useState(false);
    const endpoint = byTeam ? 'team-ranking' : 'ranking'; // team-ranking: one row per fixed team (see the format)

    const load = useCallback(async () => {
        try {
            const body = await apiRequest('GET', `/tournaments/${tournamentId}/${endpoint}`, userId);
            setStandings(body.standings || []);
            setTournament(body.tournament || null);
            setRanking(body.ranking || null);
            setFailed(false);
        } catch (error) {
            console.error('Error loading the ranking:', error);
            setFailed(true);
        } finally {
            setLoading(false);
        }
    }, [tournamentId, userId, endpoint]);

    useEffect(() => {
        if (userId && enabled) load();
    }, [userId, enabled, version, load]);

    useDataChanged(() => {
        if (userId && enabled) load();
    });

    return { standings, tournament, ranking, loading, failed, reload: load };
}
